/**
 * The Game plate: the arena, its clock, its decisions and its provenance.
 *
 * The plate is a game engine. It steps the board and paints it here, in the
 * browser, and reads one decision per step from the arena session bundled with
 * the page: the same continuous run for every visitor, played at the reference
 * demo's pace, with Space to pause, the arrow keys to change the rate and R for
 * a new board while the plate holds the focus. When the run ends, the next one
 * follows without a seam.
 *
 * What every step still carries is its provenance: the real Arc block, the
 * thought tick, the state root and the fired count the decision was made under,
 * and the decision itself - the direction with a probability per criterion, the
 * score, the loop risk, and the safety layer's ruling when it refuses a step.
 * The engine says where each answer came from (`policy` for the local policy
 * engine, `jev` for the live model) and never presents one as the other.
 *
 * Nothing here signs, sends or spends: the arena is played, not traded.
 */

import type { Block } from "../api";
import { createArena, features, step, STEPS, type Arena, type Direction } from "./arena";
import { paintArena } from "./arena-canvas";
import { tg, type GameKey } from "./copy";
import { recordRow, shortHex, type MoveRecord, type ThoughtState } from "./decide";
import type { GuardOutcome } from "./guard";
import {
  advance,
  foodVector,
  readSession,
  stepAt,
  type ArenaSession,
  type Cursor,
} from "./session";
import { parseJson } from "../json";

/** The arena is square, and the plate keeps the design's 880 x 560 frame. */
const SIZE = 17;

const FIELD_RATIO = 560 / 880;

/** The reference demo's rate, and the rates its speed keys step through. */
const RATES = [4, 8, 12, 20] as const;

const DEFAULT_RATE = 2;

/** Block timestamps kept to measure the chain's own cadence. */
const RATE_WINDOW = 12;

/** Rows visible in the provenance trail, newest first. */
const TRAIL_ROWS = 12;

const DIRECTION_KEYS: Readonly<Record<Direction, GameKey>> = {
  up: "game.dir.up",
  down: "game.dir.down",
  left: "game.dir.left",
  right: "game.dir.right",
};

function need<E extends HTMLElement>(root: ParentNode, id: string, kind: new () => E): E {
  const found = root.querySelector(`#${id}`);

  if (!(found instanceof kind)) throw new Error(`the Game plate is missing #${id}`);

  return found;
}

function setText(root: ParentNode, id: string, value: string): void {
  const found = root.querySelector(`#${id}`);

  if (found !== null) found.textContent = value;
}

/**
 * Text that arrived from outside this codebase - a model's own words, a session
 * field - is data: it is escaped before it may sit next to markup this plate
 * wrote. Nothing outside can close a tag here.
 */
function safeText(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** The OS preference the rest of the site honours: a run that starts still. */
function prefersReducedMotion(): boolean {
  return (
    window.matchMedia !== undefined && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export class GameView {
  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private session: ArenaSession | null = null;
  private cursor: Cursor = { at: 0, episode: 0 };
  private arena: Arena;
  private trail: MoveRecord[] = [];
  private last: MoveRecord | null = null;
  private tick: ThoughtState | null = null;
  private latestBlock: Block | null = null;
  private active = false;
  private paused = prefersReducedMotion();
  private rate = DEFAULT_RATE;
  private timer: number | null = null;
  private stamps: number[] = [];
  private foods = 0;
  private interventions = 0;
  private sessionError: string | null = null;
  private refused: { readonly x: number; readonly y: number } | null = null;

  constructor(root: HTMLElement, sessionUrl: string) {
    this.root = root;
    this.canvas = need(root, "arenaCanvas", HTMLCanvasElement);
    this.arena = createArena({ size: SIZE, seed: 1 });

    this.repaint();
    this.rewalk();

    const button = root.querySelector("#gBackend");

    if (button instanceof HTMLButtonElement) button.hidden = true;

    window.addEventListener("resize", () => this.repaint());
    window.addEventListener("keydown", (event) => this.onKey(event));
    void this.load(sessionUrl);
  }

  /**
   * The bundled run: the engine plays it, and reads one decision per step.
   * A page that loaded while the dev server was still waking up must not sit
   * dead, so a failed read is said out loud and tried again.
   */
  private async load(url: string): Promise<void> {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        this.session = readSession(parseJson(await response.text(), url));
        this.sessionError = null;
        this.begin();
        this.render();

        return;
      } catch (error) {
        // the reason is kept, not just printed: renderStats() shows it, so the
        // plate keeps saying why it has no run instead of flashing for one block
        this.sessionError = error instanceof Error ? error.message : "unknown";
        this.renderStats();
        await new Promise((resolve) => setTimeout(resolve, 1_500 * (attempt + 1)));
      }
    }
  }

  /** One live block: the chain's own clock, shown beside the session's steps. */
  onBlock(block: Block): void {
    this.latestBlock = block;
    this.stamps.push(block.timestamp);

    while (this.stamps.length > RATE_WINDOW) this.stamps.shift();

    this.renderStats();
  }

  /** The plate plays only while it is on screen. */
  setActive(active: boolean): void {
    if (this.active === active) return;

    this.active = active;
    this.render();

    if (active) this.repaint();
  }

  /** The site's language owner changed: every word this plate wrote is written again. */
  relabel(): void {
    this.render();
  }

  // ---- the engine ----------------------------------------------------------

  private begin(): void {
    const session = this.session;

    this.cursor = { at: 0, episode: 0 };
    this.arena = createArena({ size: session?.info.board ?? SIZE, seed: session?.info.seed ?? 1 });
    this.trail = [];
    this.last = null;
    this.foods = 0;
    this.interventions = 0;
    this.refused = null;
  }

  /** One step per tick, at the rate the player chose. */
  private walk(): void {
    if (!this.active || this.paused || this.session === null) return;

    const session = this.session;
    const move = stepAt(session, this.cursor);
    const pressed = step(this.arena, move.applied);

    this.trail.unshift(move);

    if (this.trail.length > TRAIL_ROWS) this.trail.pop();

    this.last = move;
    this.tick = move.thought;
    this.foods = pressed.arena.eaten;
    this.refused =
      move.guard.kind === "overridden"
        ? {
            x: this.arena.body[0].x + STEPS[move.decision.direction].x,
            y: this.arena.body[0].y + STEPS[move.decision.direction].y,
          }
        : null;

    if (move.guard.kind === "overridden") this.interventions += 1;

    this.arena = pressed.arena;

    const next = advance(session, this.cursor);

    this.cursor = next;

    // one run follows the next: a new board, and the session starts over. The
    // same seed, because the apples the session's steps were decided against
    // come from it - a different seed would paint a board those steps never saw
    if (next.at === 0) {
      this.arena = createArena({ size: session.info.board, seed: session.info.seed });
      this.foods = 0;
      this.refused = null;
    }

    this.render();
  }

  private rewalk(): void {
    if (this.timer !== null) window.clearInterval(this.timer);

    this.timer = window.setInterval(() => this.walk(), Math.round(1_000 / RATES[this.rate]));
  }

  // ---- the reference demo's controls ---------------------------------------

  private onKey(event: KeyboardEvent): void {
    if (!this.active) return;

    // the plate's keys belong to the plate: the canvas is focusable and takes
    // focus when it is clicked, and a Space meant for a header button or an
    // arrow meant for the page must reach the page instead
    if (!this.root.contains(document.activeElement) && event.target !== this.canvas) return;

    const target = event.target;

    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }

    if (event.key === " ") {
      event.preventDefault();
      this.paused = !this.paused;
      this.render();

      return;
    }

    if (event.key === "ArrowUp" || event.key === "+") {
      event.preventDefault();
      this.rate = Math.min(this.rate + 1, RATES.length - 1);
      this.rewalk();
      this.render();

      return;
    }

    if (event.key === "ArrowDown" || event.key === "-") {
      event.preventDefault();
      this.rate = Math.max(this.rate - 1, 0);
      this.rewalk();
      this.render();

      return;
    }

    if (event.key === "r" || event.key === "R") {
      event.preventDefault();
      this.begin();
      this.render();
    }
  }

  // ---- rendering -----------------------------------------------------------

  private render(): void {
    this.renderStats();
    this.renderState();
    this.renderDocket();
    this.renderTrail();
    this.renderControls();
    this.repaint();
  }

  private renderStats(): void {
    setText(this.root, "gFoods", String(this.foods));
    setText(
      this.root,
      "gPerFood",
      this.foods === 0 ? "-" : (this.cursor.at / this.foods).toFixed(1),
    );
    setText(this.root, "gSteps", String(this.cursor.at));
    setText(this.root, "gLength", String(this.arena.body.length));
    setText(
      this.root,
      "gBlock",
      this.latestBlock === null ? "-" : this.latestBlock.number.toLocaleString("en-US"),
    );
    setText(this.root, "gCadence", this.cadence());
    setText(this.root, "gPace", `${RATES[this.rate]} /s`);
    setText(
      this.root,
      "gProvider",
      this.last === null ? "-" : this.last.source.kind === "policy" ? "policy" : "JEV",
    );
    setText(this.root, "gInterventions", String(this.interventions));
    setText(
      this.root,
      "gSession",
      this.session !== null
        ? tg("game.session", {
            steps: String(this.session.steps.length),
            foods: String(this.session.info.foods),
            pace: String(this.session.info.pace),
          })
        : this.sessionError === null
          ? tg("game.sessionLoading")
          : tg("game.sessionFailed", { reason: this.sessionError }),
    );
  }

  private renderControls(): void {
    setText(
      this.root,
      "gControls",
      tg(this.paused ? "game.controlsPlay" : "game.controls", { pace: `${RATES[this.rate]}` }),
    );
  }

  private renderState(): void {
    // the strip is the state the step's decision saw, like the docket and the
    // provenance line beside it: the board on screen is that step's result
    const seen = this.last === null ? features(this.arena) : this.last.features;
    const tick = this.tick;

    setText(this.root, "gFood", tg("game.stateIn.food", { vector: foodVector(seen.food) }));
    setText(
      this.root,
      "gDanger",
      seen.danger.length === 0
        ? tg("game.stateIn.none")
        : tg("game.stateIn.danger", {
            list: seen.danger.map((direction) => tg(DIRECTION_KEYS[direction])).join(" · "),
          }),
    );
    setText(
      this.root,
      "gSpace",
      tg("game.stateIn.free", { free: String(seen.freeSpace), cells: String(seen.cells) }),
    );
    setText(
      this.root,
      "gDigest",
      tick === null
        ? tg("game.row.noThought")
        : tg("game.stateIn.digest", {
            digest: tick.digest,
            bias: tg(DIRECTION_KEYS[tick.bias]),
          }),
    );
    // the strip reserves the digest's track and ellipsizes what does not fit, so a
    // per-step value can never reflow the strip; hover still carries the full text
    const digest = this.root.querySelector("#gDigest");

    if (digest instanceof HTMLElement) digest.title = digest.textContent ?? "";
    setText(
      this.root,
      "gDigestNote",
      tick === null ? "" : tg("game.stateIn.note", { tick: tick.tick.toLocaleString("en-US") }),
    );
  }

  private renderDocket(): void {
    const move = this.last;

    // a fresh board has nothing decided yet: the docket must not stand there
    // holding the numbers of the run before it
    if (move === null) {
      this.blankDocket();

      return;
    }

    const direction = move.decision.direction;

    setText(this.root, "gDecision", tg("game.decision", { move: String(move.move) }));

    this.fill(
      "gProbabilities",
      (["up", "down", "left", "right"] as const)
        .map((each) => {
          const probability = move.decision.probabilities[each];
          const applied = move.applied === each;
          const refused = move.guard.kind !== "accepted" && direction === each && !applied;
          const danger = move.features.danger.includes(each);

          const tags = [
            applied ? `<i class="picked-tag">${tg("game.dir.chosen")}</i>` : "",
            refused ? `<i class="picked-tag">${tg("game.dir.refused")}</i>` : "",
          ]
            .join(" ")
            .trim();

          return bar({
            label: tg(DIRECTION_KEYS[each]),
            tag: tags,
            value: probability,
            scale: 1,
            picked: applied,
            danger,
            testid: "game-probability",
            attribute: `data-direction="${each}"`,
          });
        })
        .join(""),
    );

    this.fill(
      "gConfidence",
      [
        bar({
          label: tg("game.conf.score"),
          tag: "",
          value: move.decision.confidence,
          scale: move.decision.confidenceScale,
          picked: true,
          danger: false,
          testid: "game-confidence",
        }),
        bar({
          label: tg("game.conf.sigma"),
          tag: "",
          value: move.decision.modelConfidence,
          scale: 1,
          picked: false,
          danger: false,
        }),
      ].join(""),
    );

    this.fill(
      "gRisk",
      bar({
        label: tg("game.risk.noul"),
        tag: "",
        value: move.decision.risk,
        scale: 1,
        picked: true,
        danger: false,
        testid: "game-risk",
      }),
    );

    setText(
      this.root,
      "gPlan",
      tg("game.plan", {
        slot: String(move.slot),
        size: String(move.planSize),
        role: tg(this.roleKey(move)),
      }),
    );

    this.renderProvenance(move);
  }

  /** What the docket reads before a step is played, and after R starts a round. */
  private blankDocket(): void {
    setText(this.root, "gDecision", tg("game.decision", { move: "0" }));
    this.fill("gProbabilities", "");
    this.fill("gConfidence", "");
    this.fill("gRisk", "");
    setText(this.root, "gPlan", "");
    setText(this.root, "gProvenance", "");
  }

  private roleKey(move: MoveRecord): GameKey {
    if (move.source.kind !== "jev") return "game.role.policy";

    return move.slot === 1 ? "game.role.chosen" : "game.role.follow";
  }

  private renderProvenance(move: MoveRecord): void {
    const seen = move.features;
    const root = move.thought?.stateRoot ?? null;
    const reused = move.thought?.reused === true;
    const tick = (move.thought?.tick ?? 0).toLocaleString("en-US");
    const fired = String(move.thought?.fired ?? 0);
    const rootText = root === null ? tg("game.prov.rootPending") : safeText(shortHex(root));

    const lines = [
      tg("game.prov.block", {
        block: move.block.number.toLocaleString("en-US"),
        hash: safeText(shortHex(move.block.hash)),
        txs: String(move.block.txs),
      }),
      reused
        ? tg("game.prov.reusedThought", { tick, root: rootText, fired })
        : tg("game.prov.thought", { tick, root: rootText, fired }),
      tg("game.prov.state", {
        food: tg("game.stateIn.food", { vector: foodVector(seen.food) }),
        free: String(seen.freeSpace),
        cells: String(seen.cells),
        length: String(seen.length),
      }),
      move.source.kind === "jev"
        ? tg("game.prov.latency", {
            ms: move.source.latencyMs.toLocaleString("en-US"),
            model: safeText(move.source.model),
          })
        : tg("game.prov.policy"),
    ];

    this.fill("gProvenance", lines.join("<br />"));
  }

  private renderTrail(): void {
    const body = this.root.querySelector("#gTrail");

    if (!(body instanceof HTMLElement)) return;

    body.innerHTML = this.trail
      .map((move) => {
        const log = recordRow(move);
        const refused = log.guard.kind !== "accepted" && log.guard.kind !== "trapped";
        const thought = log.reused ? tg("game.row.tickReused", { tick: log.tick }) : log.tick;
        const root = log.root === null ? tg("game.row.rootPending") : safeText(log.root);
        const guard = this.guardLabel(log.guard);

        return `<tr data-testid="game-trail-row" data-source="${log.source}" data-guard="${log.guard.kind}">
      <td><b>${log.block}</b></td>
      <td>${thought} · ${root}</td>
      <td>${tg(DIRECTION_KEYS[log.move])}</td>
      <td>${log.slot}</td>
      <td>${log.top}</td>
      <td>${sourceTag(log.source)}</td>
      <td>${log.latencyMs === null ? "-" : `${log.latencyMs.toLocaleString("en-US")} ms`}</td>
      <td>${refused ? `<span class="tag guard">${guard}</span>` : guard}</td>
    </tr>`;
      })
      .join("");
  }

  private guardLabel(guard: GuardOutcome): string {
    if (guard.kind === "accepted") return tg("game.guard.clean");

    if (guard.kind === "trapped") return tg("game.guard.trapped");

    return guard.reason === "cycle-guard"
      ? tg("game.guard.cycle", { direction: tg(DIRECTION_KEYS[guard.direction]) })
      : tg("game.guard.fatal", { direction: tg(DIRECTION_KEYS[guard.direction]) });
  }

  private fill(id: string, html: string): void {
    const found = this.root.querySelector(`#${id}`);

    if (found !== null) found.innerHTML = html;
  }

  /** Blocks per second, measured from the chain's own block timestamps. */
  private cadence(): string {
    if (this.stamps.length < 2) return "-";

    const first = this.stamps[0];
    const last = this.stamps[this.stamps.length - 1];
    const seconds = (last ?? 0) - (first ?? 0);

    // Arc block timestamps are whole seconds shared by consecutive blocks: a
    // short window can span zero seconds, and no cadence is better than a lie
    return seconds >= 2 ? `${((this.stamps.length - 1) / seconds).toFixed(1)} /s` : "-";
  }

  private repaint(): void {
    const context = this.canvas.getContext("2d");

    if (context === null) return;

    const width = Math.max(280, Math.round(this.canvas.getBoundingClientRect().width));
    const height = Math.round(width * FIELD_RATIO);
    const ratio = Math.min(window.devicePixelRatio || 1, 2);

    if (this.canvas.width !== Math.round(width * ratio)) {
      this.canvas.width = Math.round(width * ratio);
      this.canvas.height = Math.round(height * ratio);
    }

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    paintArena({
      ctx: context,
      width,
      height,
      arena: this.arena,
      refused: this.refused,
      heading: this.last === null ? null : this.last.applied,
    });
  }
}

function bar(input: {
  readonly label: string;
  readonly tag: string;
  readonly value: number;
  readonly scale: number;
  readonly picked: boolean;
  readonly danger: boolean;
  readonly testid?: string;
  readonly attribute?: string;
}): string {
  const share = Math.max(0, Math.min(1, input.value / Math.max(1e-9, input.scale)));
  const percent = (share * 100).toFixed(1);
  const shown = input.scale > 1 ? input.value.toFixed(2) : `${Math.round(share * 100)}%`;

  const classes = ["tbar", input.picked ? "picked" : "", input.danger ? "danger" : ""]
    .filter((name) => name !== "")
    .join(" ");

  return `<div class="${classes}"${input.testid === undefined ? "" : ` data-testid="${input.testid}"`}${input.attribute === undefined ? "" : ` ${input.attribute}`}>
    <span class="tbar-k">${input.label} ${input.tag}</span>
    <span class="tbar-track"><i class="tbar-fill" style="width: ${percent}%"></i></span>
    <span class="tbar-v">${shown}</span>
  </div>`;
}

function sourceTag(source: "jev" | "plan" | "policy"): string {
  if (source === "jev") return `<span class="tag jev">${tg("game.tag.jev")}</span>`;

  if (source === "policy") return `<span class="tag">${tg("game.tag.policy")}</span>`;

  return `<span class="tag">${tg("game.tag.plan")}</span>`;
}
